import { datadogRum } from '@datadog/browser-rum';
// eslint-disable-next-line import/no-unresolved
import '@foam/blocks/css/variables.css';
import browserslist from 'browserslist';
import { matchesUA } from 'browserslist-useragent';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import badMutable from 'dayjs/plugin/badMutable';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as isLeapYear from 'dayjs/plugin/isLeapYear';
import isMoment from 'dayjs/plugin/isMoment';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import Router, { useRouter } from 'next/router';
import NProgress from 'nprogress';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { AppProviders } from '~/components/AppProviders';
import { FluidInterfaceRedirect } from '~/components/FluidInterfaceRedirect';
import Head from '~/components/Head';
import { AppHeapScript } from '~/components/HeapScript';
import facebookApi from '~/facebookApi';
import { useRouteHistory } from '~/store/routeHistory.store';
import '~/styles/globals.css';
import '~/styles/intercom.css';
import '~/styles/mediakits.css';
import '~/styles/nprogress.css';
import '~/styles/react-dates-overrides.css';
import '~/styles/transitions.css';

dayjs.extend(advancedFormat);
dayjs.extend(badMutable);
dayjs.extend(customParseFormat);
dayjs.extend(isLeapYear);
dayjs.extend(isMoment);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(isBetween);
dayjs.locale('en');

Router.events.on('routeChangeStart', () => {
  NProgress.start();
  document.body.style = 'cursor: progress';
});
Router.events.on('routeChangeComplete', (url) => {
  useRouteHistory.getState().addRoute(url);
  NProgress.done();
  document.body.style = 'cursor: initial';
});
Router.events.on('routeChangeError', () => {
  NProgress.done();
  document.body.style = 'cursor: initial';
});

if (typeof window !== 'undefined') {
  facebookApi.init();
}

const propTypes = {
  Component: PropTypes.func,
  isSupportedBrowser: PropTypes.bool,
  pageProps: PropTypes.object,
};

const App = ({ Component, isSupportedBrowser, pageProps }) => {
  const router = useRouter();

  // Record initial landing page
  useEffect(() => {
    useRouteHistory.getState().addRoute(router.asPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window && window.navigator.language) {
      dayjs.locale(window.navigator.language ?? 'en');
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-constant-condition
    if (typeof window) {
      datadogRum.setGlobalContextProperty(
        'isSupportedBrowser',
        isSupportedBrowser,
      );
    }
  }, [isSupportedBrowser]);

  return (
    <AppProviders
      dehydratedState={pageProps?.dehydratedState}
      isSSR={pageProps?.isSSR}
      isSupportedBrowser={isSupportedBrowser}
    >
      <Head />

      <AppHeapScript />
      {/*
        App-wide so every route hands off, not just the ones wrapped in
        LoggedLayout. Renders nothing (FOAM-22396).
      */}
      <FluidInterfaceRedirect />
      <Component {...pageProps} />
    </AppProviders>
  );
};

App.getInitialProps = async ({ ctx }) => {
  const userAgent = ctx.req
    ? ctx.req.headers['user-agent']
    : navigator.userAgent;

  const supportedBrowsers = browserslist();
  const isSupportedBrowser = matchesUA(userAgent, {
    browsers: supportedBrowsers,
    allowHigherVersions: true,
  });

  return { isSupportedBrowser };
};

App.propTypes = propTypes;

export default App;
